import axios from "axios";
import MathJs from "../shims/mathjs/Unit";
import qs from "qs";
import { ProductSurfacePathType } from "../types/ProductSurface";
const baseServiceURL = "https://surfaces.products.vpsvc.com";
const helperBaseServiceURL = "https://surface-helper.products.cimpress.io";
const templateGenerationServiceURL = "https://design-template-generator.products.cimpress.io";
const DEFAULT_PRECISION = 2;
export default class ProductSurfacesServiceClient {
    constructor(config) {
        this.config = config;
    }
    static getDimensionFromPath(paths) {
        // Get all the x and y coords from all paths
        const xLocations = [
            ...paths.map(path => path.anchorX),
            ...(paths.map(path => path.pathPoints.map(point => point.endPointX)).flat())
        ];
        const yLocations = [
            ...paths.map(path => path.anchorY),
            ...(paths.map(path => path.pathPoints.map(point => point.endPointY)).flat())
        ];
        const minX = Math.min(...xLocations);
        const maxX = Math.max(...xLocations);
        const minY = Math.min(...yLocations);
        const maxY = Math.max(...yLocations);
        return { height: maxY - minY, width: maxX - minX };
    }
    static convertToDifferentUnits(dimension, precision, units, locale) {
        // MathJS Shims
        const heightCm = MathJs.Unit(dimension.height, "cm");
        const widthCm = MathJs.Unit(dimension.width, "cm");
        return units.reduce((acc, unitType) => {
            acc[unitType] = {
                height: heightCm
                    .to(unitType)
                    .toNumber(unitType)
                    .toLocaleString(locale, {
                    maximumFractionDigits: precision,
                }),
                width: widthCm
                    .to(unitType)
                    .toNumber(unitType)
                    .toLocaleString(locale, {
                    maximumFractionDigits: precision,
                }),
            };
            return acc;
        }, {});
    }
    async fetchSurfacesAndSvg(options) {
        const { productKey, productOptions, units, locale, bearerToken, version } = options;
        const axiosRequestConfig = {};
        if (bearerToken !== undefined) {
            axiosRequestConfig.headers = {
                Authorization: bearerToken,
                Accept: "application/json",
            };
        }
        const queryString = qs.stringify({
            optionSelections: productOptions,
            requestor: this.config.requestor,
        });
        const surfacesUrl = `${baseServiceURL}/surfaces/${productKey}${version ? `/${version}` : ""}?${queryString}`;
        const surfacesInfo = await this.makeProductSurfacesCall(surfacesUrl, axiosRequestConfig, units, locale);
        const svgUrl = await this.fetchSvgTemplateUrl(surfacesUrl, axiosRequestConfig);
        return {
            svgUrl,
            surfacesInfo,
        };
    }
    async fetchProductSurfaces(options) {
        const { productKey, productOptions, units, locale, bearerToken, version } = options;
        const axiosRequestConfig = {};
        if (bearerToken !== undefined) {
            axiosRequestConfig.headers = {
                Authorization: bearerToken,
                Accept: "application/json",
            };
        }
        const queryString = qs.stringify({
            optionSelections: productOptions,
            requestor: this.config.requestor,
        });
        const url = `${baseServiceURL}/surfaces/${productKey}${version ? `/${version}` : ""}?${queryString}`;
        return await this.makeProductSurfacesCall(url, axiosRequestConfig, units, locale);
    }
    async makeProductSurfacesCall(url, config, units, locale) {
        return axios
            .get(url, config)
            .then(response => {
            // confirm that the response is valid
            if (!(response.data !== undefined &&
                Array.isArray(response.data.surfaceGroups) &&
                response.data.surfaceGroups.length > 0 &&
                Array.isArray(response.data.surfaceGroups[0].surfaces) &&
                response.data.surfaceGroups[0].surfaces.length > 0 &&
                response.data.surfaceGroups[0].surfaces[0].docAdditionalData !== undefined &&
                Array.isArray(response.data.surfaceGroups[0].surfaces[0].docAdditionalData.masks))) {
                throw new Error(`Malformed product surfaces response: ${response}`);
            }
            const { data } = response;
            // Assuming all surfaces have the same dimension
            const anySurface = data.surfaceGroups[0].surfaces[0];
            const { masks } = anySurface.docAdditionalData;
            const pathTypes = [
                ProductSurfacePathType.BLEED,
                ProductSurfacePathType.TRIM,
                ProductSurfacePathType.SAFE,
                ProductSurfacePathType.CUT,
            ];
            return pathTypes.reduce((acc, pathType) => {
                const mask = masks.find(m => m.pathType === pathType);
                if (mask === undefined || !Array.isArray(mask.paths) || mask.paths.length === 0) {
                    return acc;
                }
                const dimension = ProductSurfacesServiceClient.getDimensionFromPath(mask.paths);
                acc[pathType] = ProductSurfacesServiceClient.convertToDifferentUnits(dimension, DEFAULT_PRECISION, units, locale);
                return acc;
            }, {});
        });
    }
    async fetchSvgTemplateUrl(calculatedSurfaceSetUrl, config) {
        var _a;
        const url = `${helperBaseServiceURL}/v0/svg:zip`;
        const requestBody = {
            calculatedSurfaceSetUrl
        };
        try {
            const svgZipUrl = (_a = (await axios.post(url, requestBody, config)).data) === null || _a === void 0 ? void 0 : _a.file;
            return svgZipUrl;
        }
        catch (ex) {
            console.error("Failed to get svg url", ex);
        }
        return undefined;
    }
    async fetchAiTemplateUrl(calculatedSurfaceSetUrl, config) {
        const resp = await axios.post(`${templateGenerationServiceURL}/v0/generateAi?asynchronous=true`, { calculatedSurfaceSetUrl }, config);
        if (resp.data.Status === "Completed") {
            return resp.data.ResultUrl;
        }
        return undefined;
    }
    async fetchPdfTemplateUrl(calculatedSurfaceSetUrl, config) {
        const resp = await axios.post(`${templateGenerationServiceURL}/v1/designTemplateGenerator`, { calculatedSurfaceSetUrl }, config);
        if (resp.data.Status === "Completed") {
            return resp.data.PdfUrl;
        }
        return undefined;
    }
    async fetchProductSurfacesRenderingURL(options) {
        const { productKey, productOptions, imageWidth, safeAreaText, bearerToken, rotate90, version } = options;
        const axiosRequestConfig = {};
        if (bearerToken !== undefined) {
            axiosRequestConfig.headers = {
                Authorization: bearerToken,
                Accept: "application/json",
            };
        }
        const queryString = qs.stringify({
            optionSelections: productOptions,
            requestor: this.config.requestor,
            safeAreaName: safeAreaText,
            rotate90,
        });
        const url = `${baseServiceURL}/surfaces/${productKey}${version ? `/${version}` : ""}/sampleimage/${imageWidth}?${queryString}`;
        return axios
            .get(url, axiosRequestConfig)
            .then(response => {
            if (!response.data.url) {
                return undefined;
            }
            return response.data.url;
        })
            .catch(() => {
            return undefined;
        });
    }
    async fetchSurfacesAndUrls(options) {
        const { productKey, productOptions, units, locale, bearerToken, version } = options;
        const axiosRequestConfig = {};
        if (bearerToken !== undefined) {
            axiosRequestConfig.headers = {
                Authorization: bearerToken,
                Accept: "application/json",
            };
        }
        const queryString = qs.stringify({
            optionSelections: productOptions,
            requestor: this.config.requestor,
        });
        const surfacesUrl = `${baseServiceURL}/surfaces/${productKey}${version ? `/${version}` : ""}?${queryString}`;
        const surfacesInfo = await this.makeProductSurfacesCall(surfacesUrl, axiosRequestConfig, units, locale);
        const urlPromises = [
            this.fetchSvgTemplateUrl(surfacesUrl, axiosRequestConfig),
            this.fetchAiTemplateUrl(surfacesUrl, axiosRequestConfig),
            this.fetchPdfTemplateUrl(surfacesUrl, axiosRequestConfig)
        ];
        const [svgUrl, aiUrl, pdfUrl] = await Promise.allSettled(urlPromises);
        return {
            svgUrl: svgUrl.status === "fulfilled" ? svgUrl.value : "",
            aiUrl: aiUrl.status === "fulfilled" ? aiUrl.value : "",
            pdfUrl: pdfUrl.status === "fulfilled" ? pdfUrl.value : "",
            surfacesInfo,
        };
    }
}
//# sourceMappingURL=ProductSurfaceService.js.map